import type { SaveDraftConversationRequest } from '@crowdex/agent-contracts/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { saveDraftConversation } from '@/lib/conversations/conversation-changes-client';
import { desktopWorkspaceName } from '@/lib/desktop-shell/personal-workspace';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';

/**
 * The conversation an app's tab works in. An app docked in a conversation (its New tab, an app
 * home's "New …") is a tab of the conversation shown, and works in that conversation's workspace: its folder on this computer,
 * its sandbox in the cloud. A new conversation has none until it is saved, which its first message
 * did alone; an app opened in it saves it first, where it will run (the folder and agent chosen in
 * its composer), so the app shows its own content rather than failing on its first file.
 */
export type AppConversation =
  | { state: 'saved'; conversation: string }
  | { state: 'saving' }
  | { state: 'failed'; message: string; retry: () => void }
  /** No conversation to save (the preview): the app opens without a workspace. */
  | { state: 'none' };

/** Where a new conversation will run, as its composer chose it. */
export function draftPlace(
  state: ReturnType<DesktopShellStore['getState']>,
): SaveDraftConversationRequest['runsIn'] {
  const agent = state.agentId === '' ? null : state.agentId;
  if (state.executionMode === 'cloud') return { kind: 'cloud', agent };
  const root = state.workspace;
  return {
    kind: 'this-computer',
    folder:
      root === null
        ? null
        : { root, name: desktopWorkspaceName(root, state.personalWorkspaceRoot).slice(0, 80) },
    runtime: state.executionMode === 'worktree' ? 'worktree' : 'local',
    agent,
  };
}

const saving = new Map<string, Promise<void>>();

/**
 * Saves `conversation` if it is not yet (one request at a time for it, whoever asks), and lists it
 * among the synced conversations, which is how the shell knows it has a workspace.
 */
export function ensureConversationSaved(
  store: DesktopShellStore,
  conversation: string,
  title: string,
): Promise<void> {
  if (store.getState().syncedThreads.some((thread) => thread.id === conversation))
    return Promise.resolve();
  const pending = saving.get(conversation);
  if (pending !== undefined) return pending;
  const request = saveDraftConversation(conversation, {
    title,
    runsIn: draftPlace(store.getState()),
  })
    .then((saved) => {
      const now = new Date().toISOString();
      store.getState().setSyncedThreads((current) =>
        current.some((thread) => thread.id === saved.conversation)
          ? current
          : [
              {
                id: saved.conversation,
                runtime: saved.runtime,
                workspaceId: saved.workspaceId,
                title: saved.title,
                updatedAt: now,
                lastMessageAt: null,
                messageRevision: 0,
              },
              ...current,
            ],
      );
    })
    .finally(() => saving.delete(conversation));
  saving.set(conversation, request);
  return request;
}

/**
 * The conversation an app's tab works in, saved for it when needed (see {@link AppConversation}).
 * `title` names a conversation saved this way until its first message names it.
 */
export function useAppConversation(
  conversation: string | undefined,
  title: string,
  enabled = true,
): AppConversation {
  const store = useDesktopShellStoreApi();
  const saved = useDesktopShellStore((state) =>
    conversation === undefined
      ? false
      : state.syncedThreads.some((thread) => thread.id === conversation),
  );
  const [failure, setFailure] = useState<{ conversation: string; message: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const titleRef = useRef(title);
  titleRef.current = title;
  const wanted = enabled && conversation !== undefined && !saved;
  useEffect(() => {
    if (!wanted || conversation === undefined) return;
    let live = true;
    setFailure(null);
    // `attempt` asks again after a failure.
    void attempt;
    ensureConversationSaved(store, conversation, titleRef.current).catch((cause: unknown) => {
      if (live)
        setFailure({
          conversation,
          message: cause instanceof Error ? cause.message : String(cause),
        });
    });
    return () => {
      live = false;
    };
  }, [wanted, conversation, store, attempt]);
  const retry = useCallback(() => setAttempt((current) => current + 1), []);
  if (conversation === undefined || !enabled) return { state: 'none' };
  if (saved) return { state: 'saved', conversation };
  if (failure !== null && failure.conversation === conversation)
    return { state: 'failed', message: failure.message, retry };
  return { state: 'saving' };
}
